import { createElement, forwardRef, useState } from 'react';

import type {
  AnyClaimPaymentOut,
  ClaimKey,
  ClaimcaseKey,
  PaymentKey,
  PaymentStatus,
} from '../../../client/portal/types.gen';
import PaymentTableRowNew from './PaymentTableRow';
import GridTable from '../../../components/table/GridTable';
import { useSettings } from '../../../context/settingsContext';
import styled from 'styled-components';
import Button from '../../../components/Button';
import Icon from '../../../components/Icon';
import { exportPaymentsService } from '../../../services/portalServices';
import { useNotification } from '../../../context/notificationContext';
import DatePicker from 'react-datepicker';
import formatDate from '../../../utils/formatDate';
import { getCurrency } from '../../../utils/getCurrency';

type PaymentsTableProps = {
  category: string;
  getPayments: () => Promise<void>;
  payments: AnyClaimPaymentOut[];
  tag: string;
};

export type PaymentTableRowContent = {
  amount: number;
  claimcaseKey: ClaimcaseKey;
  claimKey: ClaimKey;
  currency: string;
  dueDate: string;
  frontendBaseUrl: string;
  hasPaymentIntegration: boolean;
  message: string;
  method: string;
  partner: string;
  partnerApiUrl: string;
  payment: AnyClaimPaymentOut;
  paymentKey: PaymentKey;
  paymentRecipient: string;
  rejectedReason: string;
  requestedBy: string;
  selected: boolean;
  signedBy: string;
  status: PaymentStatus;
};

const PickDate = forwardRef(
  ({ onClick }: { onClick: () => Promise<void> }, ref) => {
    return (
      <>
        <Button onClick={onClick}>
          <Icon iconName='faCalendarAlt' /> Select date
        </Button>
      </>
    );
  },
);

const PaymentsTable = ({
  tag,
  category,
  payments,
  getPayments,
}: PaymentsTableProps) => {
  const { partners } = useSettings();
  const { addNotification, removeNotificationWithDelay } = useNotification();
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const [selectedPayments, setSelectedPayments] = useState<PaymentKey[]>([]);
  const [sortOptions, setSortOptions] = useState<{
    column: string;
    direction: 'asc' | 'desc';
  }>({ column: 'dueDate', direction: 'asc' });

  const tableData: PaymentTableRowContent[] = payments.map((payment) => ({
    claimcaseKey: payment.claimcaseKey,
    claimKey: payment.claimKey,
    method: payment.payment.method.tag.substring('Payment'.length),
    paymentKey: payment.payment.key,
    paymentRecipient:
      payment.payment.method.tag !== 'PaymentDEManual' &&
      payment.payment.method.tag !== 'PaymentNOManual' &&
      payment.payment.method.tag !== 'PaymentSEManual'
        ? payment.payment.method.recipientName
        : '',
    message:
      payment.payment.method.tag === 'PaymentDEIBAN' ||
      payment.payment.method.tag === 'PaymentSEBBAN' ||
      payment.payment.method.tag === 'PaymentSEIBAN'
        ? payment.payment.method.creditMessage
        : payment.payment.method.tag === 'PaymentNOBankTransfer' ||
            payment.payment.method.tag === 'PaymentSEBankgiro' ||
            payment.payment.method.tag === 'PaymentSEPlusGiro'
          ? payment.payment.method.remittanceInfo.contents
          : payment.payment.method.tag === 'PaymentDEManual' ||
              payment.payment.method.tag === 'PaymentNOManual' ||
              payment.payment.method.tag === 'PaymentSEManual'
            ? payment.payment.method.contents
            : '',
    dueDate:
      payment.payment.method.tag !== 'PaymentDEManual' &&
      payment.payment.method.tag !== 'PaymentNOManual' &&
      payment.payment.method.tag !== 'PaymentSEManual'
        ? payment.payment.method.executionDay
        : '',
    rejectedReason:
      payment.payment.status.tag === 'PaymentSeb' &&
      payment.payment.status.paymentSebStatus.tag === 'PaymentSebRejected'
        ? payment.payment.status.paymentSebStatus.sebInfoReason
        : '',
    requestedBy: payment.payment.requestedBy,
    // Only the PaymentScheduled status carries signedBy; for payments further
    // along (PaymentSeb, PaymentFulfilled) it is on the payment itself.
    signedBy:
      payment.payment.status.tag === 'PaymentScheduled'
        ? payment.payment.status.signedBy
        : (payment.payment.signedBy ?? ''),
    amount: payment.payment.amount,
    currency: getCurrency(payment.payment.locale),
    status: payment.payment.status,
    payment: payment,
    partner: payment.partnerName,
    frontendBaseUrl: partners
      ? (partners.find(
          (partnerItem) => partnerItem.name === payment.partnerName,
        )?.frontendBaseUrl ?? '')
      : '',
    partnerApiUrl: partners
      ? (partners.find(
          (partnerItem) => partnerItem.name === payment.partnerName,
        )?.apiBaseUrl ?? '')
      : '',
    hasPaymentIntegration: payment.hasPaymentIntegration,
    selected: selectedPayments.find(
      (selectedPayment) => selectedPayment === payment.payment.key,
    )
      ? true
      : false,
  }));

  const columnOptions: {
    type: 'boolean' | 'number' | 'string';
    name: string;
    label: string | JSX.Element;
    isSortable?: boolean;
    width?: string | null;
    textAlign?: 'center' | 'left' | 'right' | null;
  }[] = [];

  tag === 'PaymentScheduled' &&
    columnOptions.push({
      name: 'selected',
      label: '',
      type: 'boolean',
      isSortable: false,
    });

  columnOptions.push({
    name: 'partner',
    label: 'Partner',
    type: 'string',
    isSortable: true,
  });
  columnOptions.push({
    name: 'claimcaseKey',
    label: 'Claim case',
    type: 'string',
    isSortable: true,
  });
  columnOptions.push({
    name: 'method',
    label: 'Method',
    type: 'string',
    isSortable: true,
  });
  columnOptions.push({
    name: 'paymentRecipient',
    label: 'Payment recipient',
    type: 'string',
    isSortable: true,
  });
  columnOptions.push({
    name: 'message',
    label: 'KID/Messeage/OCR',
    type: 'string',
    isSortable: true,
  });
  columnOptions.push({
    name: 'dueDate',
    label: 'Due date',
    type: 'string',
    isSortable: true,
  });
  tag === 'PaymentSebRejected' &&
    columnOptions.push({
      name: 'rejectedReason',
      label: 'Rejected reason',
      type: 'string',
      isSortable: true,
    });
  columnOptions.push({
    name: 'requestedBy',
    label: 'Requested by',
    type: 'string',
    isSortable: true,
  });
  columnOptions.push({
    name: 'signedBy',
    label: 'Signed by',
    type: 'string',
    isSortable: true,
  });
  columnOptions.push({
    name: 'currency',
    label: 'Currency',
    type: 'string',
    isSortable: true,
  });
  columnOptions.push({
    name: 'amount',
    label: 'Amount',
    type: 'number',
    textAlign: 'right',
    isSortable: true,
  });
  columnOptions.push({
    name: '',
    label: '',
    type: 'string',
    textAlign: 'left',
    isSortable: false,
  });

  const handleSelectAllClick = () => {
    payments.forEach((payment) => {
      payment.hasPaymentIntegration &&
        !selectedPayments.find(
          (selectedPayment) => selectedPayment === payment.payment.key,
        ) &&
        setSelectedPayments((state) => [...state, payment.payment.key]);
    });
  };

  const handleSelectByDateClick = (date: Date | null) => {
    if (!date) return;

    const paymentsToSelect = payments
      .filter(
        (payment) =>
          payment.hasPaymentIntegration &&
          payment.payment.method.tag !== 'PaymentDEManual' &&
          payment.payment.method.tag !== 'PaymentNOManual' &&
          payment.payment.method.tag !== 'PaymentSEManual' &&
          payment.payment.method.executionDay <= formatDate(date),
      )
      .map((payment) => payment.payment.key);

    setSelectedPayments(paymentsToSelect);
  };

  const handleUnselectAllClick = () => setSelectedPayments([]);

  const onSelectPayment = (paymentKey: PaymentKey) =>
    setSelectedPayments((state) => [...state, paymentKey]);

  const onDeselectPayment = (paymentKey: PaymentKey) =>
    setSelectedPayments(
      selectedPayments.filter(
        (selectedPayment) => selectedPayment !== paymentKey,
      ),
    );

  const handleExportPaymentsClick = async () => {
    setIsPosting(true);

    try {
      await exportPaymentsService(selectedPayments);

      alert('Payments has been sent to SEB.');
      setSelectedPayments([]);
      removeNotificationWithDelay(
        addNotification('Payments has been sent to SEB', 'success'),
      );

      getPayments();
    } catch (error) {
      removeNotificationWithDelay(
        addNotification(
          `Something went wrong when trying to sent payments to SEB: ${
            error instanceof Error ? error.message : String(error)
          }`,
          'alert',
        ),
      );
    } finally {
      setIsPosting(false);
    }
  };

  const uniqueDueDates = payments
    .filter(
      (payment) =>
        payment.payment.method.tag !== 'PaymentDEManual' &&
        payment.payment.method.tag !== 'PaymentNOManual' &&
        payment.payment.method.tag !== 'PaymentSEManual',
    )
    .map((payment) => (payment.payment.method as any).executionDay)
    .filter((date, index, array) => array.indexOf(date) === index);

  return (
    <>
      {category === 'PaymentScheduled' && (
        <ButtonsWrapper>
          <SelectionsButtonsWrapper>
            <Button onClick={handleSelectAllClick} disabled={isPosting}>
              <Icon iconName='faSquareCheck' /> Select all
            </Button>
            <Button
              onClick={handleUnselectAllClick}
              disabled={selectedPayments.length === 0 || isPosting}
            >
              <Icon iconName='faSquare' /> Unselect all
            </Button>
            <div style={{ display: 'flex' }}>
              <DatePicker
                className='default'
                locale='sv'
                dateFormat='yyyy-MM-dd'
                onChange={handleSelectByDateClick}
                customInput={createElement(PickDate)}
                includeDateIntervals={uniqueDueDates.map((date) => {
                  const [year, month, day] = date.split('-');
                  const dateObj = new Date(year, month - 1, day);
                  return {
                    start: dateObj,
                    end: dateObj,
                  };
                })}
              />
            </div>
          </SelectionsButtonsWrapper>
          <ActionsButtonsWrapper>
            <Button
              theme='green'
              disabled={selectedPayments.length === 0 || isPosting}
              onClick={handleExportPaymentsClick}
            >
              Send selected payments to SEB
            </Button>
          </ActionsButtonsWrapper>
        </ButtonsWrapper>
      )}
      <GridTable
        columnOptions={columnOptions}
        tableData={tableData}
        Row={PaymentTableRowNew}
        sortOptions={sortOptions}
        setSortOption={setSortOptions}
        tableRowCustomProps={{
          getPayments,
          onSelectPayment,
          onDeselectPayment,
          isPosting,
        }}
        isLoading={false}
      />
    </>
  );
};

export default PaymentsTable;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: stretch;
  width: 100%;
  justify-content: space-between;
`;

const SelectionsButtonsWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionsButtonsWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`;
